import { Navigate, type RouteObject } from "react-router";

import { DayPage } from "../pages/DayPage";
import { HealthPage } from "../pages/health/HealthPage";
import { AutotaskSettings } from "../pages/mappings/AutotaskSettings";
import { ClientMappingSection } from "../pages/mappings/ClientMapping";
import { CountingSettings, VendorSettings } from "../pages/mappings/CountingSettings";
import { LoggingSettings } from "../pages/mappings/LoggingSettings";
import { MappingsPage } from "../pages/mappings/MappingsPage";
import { TechMappingSection } from "../pages/mappings/TechMapping";
import { NetworkSettings } from "../pages/mappings/NetworkSettings";
import { MySettingsPage } from "../pages/my-settings/MySettingsPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { ClientAuditReport } from "../pages/reports/ClientAuditReport";
import { ManagedEffortReport } from "../pages/reports/ManagedEffortReport";
import { OpenTicketsReport } from "../pages/reports/OpenTicketsReport";
import { ReportsPage } from "../pages/reports/ReportsPage";
import { TechActivityReport } from "../pages/reports/TechActivityReport";
import { UnsubmittedAgingReport } from "../pages/reports/UnsubmittedAgingReport";
import { UtilizationReport } from "../pages/reports/UtilizationReport";
import { SettingsPage } from "../pages/settings/SettingsPage";
import { TeamPage } from "../pages/team/TeamPage";
import { OwnerToolsPage } from "../pages/tools/OwnerToolsPage";
import { Layout } from "./Layout";
import { RequireOwner } from "./RequireOwner";

export const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      { index: true, element: <DayPage /> },
      {
        // A tech's of their own work, an owner's of anyone's (section 6.2;
        // ADR 0123): the API decides. Each report is a child route.
        path: "reports",
        element: <ReportsPage />,
        children: [
          { index: true, element: <TechActivityReport /> },
          { path: "utilization", element: <UtilizationReport /> },
          { path: "managed-effort", element: <ManagedEffortReport /> },
          { path: "unsubmitted-aging", element: <UnsubmittedAgingReport /> },
          { path: "open-tickets", element: <OpenTicketsReport /> },
          { path: "client-audit", element: <ClientAuditReport /> },
        ],
      },
      {
        path: "team",
        element: (
          <RequireOwner>
            <TeamPage />
          </RequireOwner>
        ),
      },
      {
        path: "mappings",
        element: (
          <RequireOwner>
            <MappingsPage />
          </RequireOwner>
        ),
        children: [
          { index: true, element: <ClientMappingSection /> },
          // Workdays went to Settings, beside each tech's hours (ADR 0198).
          { path: "techs", element: <TechMappingSection /> },
          // Unmapped names became the master table's ScreenConnect column (ADR 0078).
          {
            path: "unmapped-names",
            element: <Navigate to="/mappings?screenconnect=unmapped" replace />,
          },
          // The Autotask IDs, then Logging to Autotask, from Settings (ADR 0198).
          {
            path: "autotask",
            element: (
              <>
                <AutotaskSettings />
                <LoggingSettings />
              </>
            ),
          },
          // Counting became Email, with the vendors' domains (ADR 0132).
          { path: "email", element: <CountingSettings /> },
          { path: "counting", element: <Navigate to="/mappings/email" replace /> },
          // Vendors' email domains and phone numbers, from Email (ADR 0198).
          { path: "vendors", element: <VendorSettings /> },
          // Which network connections count (Phase 7; ADR 0158).
          { path: "network", element: <NetworkSettings /> },
        ],
      },
      {
        path: "health",
        element: (
          <RequireOwner>
            <HealthPage />
          </RequireOwner>
        ),
      },
      {
        // Every owner setting without a place on Mappings (ADR 0172).
        path: "settings",
        element: (
          <RequireOwner>
            <SettingsPage />
          </RequireOwner>
        ),
      },
      // Each user's own, owners too, from the user menu (ADR 0200).
      { path: "my-settings", element: <MySettingsPage /> },
      // Not in the main menu: the backtick key and the user menu's Owner tools
      // open the tools over the page, for owners (ADR 0098; #661).
      {
        path: "tools",
        element: (
          <RequireOwner>
            <OwnerToolsPage />
          </RequireOwner>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
